// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/whatsappCloud/embeddedSignup.ts
//  Dispara o Embedded Signup oficial da Meta (WhatsApp Business Platform) no
//  navegador, direto do SPA — equivalente ao FrmConectarWhatsApp do Desktop.
//
//  Carrega o JS SDK do Facebook, chama FB.login com o config_id do App e escuta
//  o evento WA_EMBEDDED_SIGNUP para capturar waba_id/phone_number_id. Devolve
//  { code, wabaId, phoneNumberId } para o backend trocar o code por token.
//
//  ⚠️ Para o FB.login abrir, o domínio que serve o SPA (app.udisysgestor.com)
//  precisa estar em "Allowed Domains for the JavaScript SDK" no App da Meta.
// ════════════════════════════════════════════════════════════════════════════

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<void> | null = null;

/** Carrega e inicializa o SDK da Meta uma única vez (single-flight). */
function carregarSdk(appId: string, versao: string): Promise<void> {
  if (window.FB) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve, reject) => {
    window.fbAsyncInit = () => {
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: versao });
        resolve();
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Falha ao inicializar o SDK da Meta.'));
      }
    };

    const id = 'facebook-jssdk';
    if (document.getElementById(id)) return; // script já injetado; fbAsyncInit resolve
    const js = document.createElement('script');
    js.id = id;
    js.async = true;
    js.defer = true;
    js.crossOrigin = 'anonymous';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => {
      sdkPromise = null; // permite nova tentativa
      reject(new Error('Não foi possível carregar o SDK da Meta (verifique a conexão / bloqueadores).'));
    };
    document.body.appendChild(js);
  });

  return sdkPromise;
}

export interface SignupResultado {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

/** Origem confiável = exatamente facebook.com ou um subdomínio dele. */
function daMeta(origem: string): boolean {
  try {
    const h = new URL(origem).hostname;
    return h === 'facebook.com' || h.endsWith('.facebook.com');
  } catch {
    return false;
  }
}

/**
 * Abre o Embedded Signup e resolve com o code + waba_id + phone_number_id.
 * Coexistência (featureType whatsapp_business_app_onboarding): o número segue no
 * app WhatsApp Business do cliente; sessionInfoVersion:'3' faz a Meta devolver
 * waba_id/phone_number_id no evento FINISH.
 */
export async function conectarWhatsApp(
  appId: string,
  configId: string,
  versao: string,
): Promise<SignupResultado> {
  if (!appId || !configId) {
    throw new Error('Conexão automática indisponível: o App da Meta não está configurado no servidor.');
  }

  await carregarSdk(appId, versao || 'v26.0');

  return new Promise<SignupResultado>((resolve, reject) => {
    let code: string | null = null;
    let wabaId: string | null = null;
    let phoneNumberId: string | null = null;
    let concluido = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const encerrar = () => {
      window.removeEventListener('message', onMessage);
      if (timer) { clearTimeout(timer); timer = null; }
    };
    const ok = () => {
      if (concluido || !(code && wabaId && phoneNumberId)) return;
      concluido = true;
      encerrar();
      resolve({ code, wabaId, phoneNumberId });
    };
    const falhar = (msg: string) => {
      if (concluido) return;
      concluido = true;
      encerrar();
      reject(new Error(msg));
    };

    const onMessage = (ev: MessageEvent) => {
      if (!daMeta(ev.origin)) return;
      try {
        const data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
        if (!data || data.type !== 'WA_EMBEDDED_SIGNUP' || !data.data) return;
        // Coexistência termina com FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING (não 'FINISH').
        if (String(data.event).indexOf('FINISH') === 0 && data.data.waba_id) {
          wabaId = data.data.waba_id;
          phoneNumberId = data.data.phone_number_id ?? phoneNumberId;
          ok();
        } else if (data.event === 'CANCEL') {
          falhar('Conexão cancelada. Você pode tentar de novo.');
        }
      } catch {
        /* evento sem JSON — ignora */
      }
    };

    window.addEventListener('message', onMessage);

    try {
      window.FB.login(
        (resp: any) => {
          if (resp && resp.authResponse && resp.authResponse.code) {
            code = resp.authResponse.code;
            ok();
            // Code veio mas a Meta não mandou waba/phone: quase sempre a janela foi
            // fechada antes do "Concluir". Sem isso o botão ficaria girando pra sempre.
            timer = setTimeout(() => {
              falhar('A Meta não informou a conta/número. Refaça e clique em "Concluir" na última tela (não feche no X).');
            }, 12000);
          } else {
            falhar('Login cancelado ou sem autorização.');
          }
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            version: 'v4',
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: '3',
          },
        },
      );
    } catch (e) {
      falhar(e instanceof Error ? e.message : 'Falha ao abrir o fluxo da Meta.');
    }
  });
}
